import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Section, getJumpMenuNavItems } from "./getJumpMenuNavItems";
import { useUserRoles } from "../hooks/useUserRoles";
// import { useSummaryData } from "./useSummaryData";

export function useGetSections(_open: boolean | undefined) {
  const { t } = useTranslation();
  const roles = useUserRoles();
  // Query combined summary (patients + consultations)
  // const { recentPatientsItems, recentConsultationsItems } = useSummaryData(open);
  const recentPatientsItems: any[] = [];
  const recentConsultationsItems: any[] = [];

  // Memoize expensive section construction and translation to avoid
  // re-computing on every render when inputs haven't changed.
  const sections = useMemo<Section[]>(() => {
    const computed = getJumpMenuNavItems(
      recentPatientsItems,
      recentConsultationsItems
    );

    // translate section titles
    computed.forEach((section) => {
      section.label = t(section.titleKey, {
        defaultValue: section.titleDefault,
      });
    });

    // translate item labels
    computed.forEach((section) => {
      section.items.forEach((item) => {
        // Avoid requesting translations for dynamic keys created at runtime
        // (patients, consultations, or transient keys like _loading/_error/_empty).
        // Calling `t` with lots of unique keys floods the i18next missingKey logs.
        const isDynamicKey = /^(patient_|consult_)|_(loading|error|empty)$/.test(
          item.key
        );
        if (isDynamicKey) {
          item.label = item.defaultValue;
        } else {
          item.label = t(`jumpMenu.${item.key}`, {
            defaultValue: item.defaultValue,
          });
        }
      });
    });

    return computed;
  }, [recentPatientsItems, recentConsultationsItems, t]);

  const lowerRoles = roles.map((r) => r.toLowerCase());
  const filtered = sections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => {
        if (!item.role && !item.roles) return true;
        if (item.role && lowerRoles.includes(item.role.toLowerCase())) return true;
        if (item.roles) {
          return item.roles.some((r) => lowerRoles.includes(r.toLowerCase()));
        }
        return false;
      }),
    }))
    .filter((section) => section.items.length > 0);

  return filtered;
}
